import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightTooltip from '@/components/utils/LightTooltip';
import { updateEmergencyConfigViaAdmin } from '@/repository/UserDAO';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { useTranslation } from 'react-i18next';
import { ErrorStringKeys, SuccessStringKeys } from '@/resources/strings/StringKeys';
import LoadingData from '../loading/LoadingData';
import BaseModal from '@/components/modals/BaseModal';
import RadioOptionBox from '@/components/form_elements/RadioOptionBox';
import COLORS from '@/resources/style/Themes';
import type { OrgUser } from '@/models/User/OrgUser';
import { SubscriptionTier } from '@/../generated-client/models';
import { getUserEffectiveTier, TierListsShape } from '@/utils/organization/getUserAssignedTier';
import { parseApiError } from '@/utils/api/parseApiError';
import PlanExplainer from '@/components/entitlements/PlanExplainer';

interface EmergencyConfigItem {
  id: string;
  name: string;
  is_base_config?: boolean;
}

const DEFAULT_SELECTOR_FONT_SIZE = 15;

interface Props {
  userToHighlight?: (authId: string) => void;
  orgCode: string;
  configList: EmergencyConfigItem[];
  user: OrgUser;
  /** The target user's org groups (auth-id pools), used to derive their assigned tier (Task 18). */
  groups?: TierListsShape[];
  /** Read-only viewers (e.g. Dispatch) cannot mutate the config — see Task 20/WD4. */
  disabled?: boolean;
  /** True while the org's trial is active; elevates every target user to Respond (nm-3). */
  isOrgOnTrial?: boolean;
  fontSize?: number;
}

const EmergencyConfigPicker: React.FC<Props> = ({
  userToHighlight = () => {},
  orgCode,
  configList,
  user,
  groups = [],
  disabled = false,
  isOrgOnTrial = false,
  fontSize = DEFAULT_SELECTOR_FONT_SIZE,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [prevId, setPrevId] = useState<string>('');
  const [pendingConfig, setPendingConfig] = useState<EmergencyConfigItem | null>(null);
  const [shouldForceApply, setShouldForceApply] = useState(false);
  const [assignmentError, setAssignmentError] = useState<'PLAN_RESPOND_REQUIRED' | null>(null);
  const latestSelectionRef = useRef(selectedId);
  const { showSuccessToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const { palette } = useTheme();
  const confirmOpen = useMemo(() => pendingConfig !== null, [pendingConfig]);

  // Emergency configs require the Respond tier specifically (spec §5.5) — not just
  // "Ops or above" — for the TARGET user being assigned the config, derived
  // from their own group membership rather than the viewer's useEntitlements().
  const targetTier = useMemo(
    () => getUserEffectiveTier(groups, user?.auth_id ?? '', isOrgOnTrial),
    [groups, user, isOrgOnTrial],
  );
  const canBeAssignedToRespondConfig = targetTier === SubscriptionTier.Respond;

  useEffect(() => {
    if (user) {
      const id = user.emergency_config_id || '';
      setSelectedId(id);
      setPrevId(id);
    }
  }, [user]);

  const updateUser = async (newConfig: EmergencyConfigItem, forceApply: boolean) => {
    if (disabled) return;
    setPrevId(selectedId);
    setAssignmentError(null);
    latestSelectionRef.current = newConfig.id;
    setLoading(true);
    const userName = `${user.first_name} ${user.last_name}`;
    try {
      await updateEmergencyConfigViaAdmin({
        org_code: orgCode,
        auth_id: user.auth_id,
        new_emergency_config_id: newConfig.id,
        should_force_apply: forceApply,
      });
      if (latestSelectionRef.current === newConfig.id) {
        setSelectedId(newConfig.id);
        userToHighlight(user.auth_id);
        showSuccessToast(
          t(SuccessStringKeys.SUCCESSFULLY_UPDATED_USERS_EMERGENCY_CONFIGURATION, { user: userName, configuration: newConfig.name }),
        );
      }
    } catch (error) {
      setSelectedId(prevId);
      const { code } = await parseApiError(error);
      if (code === 'PLAN_RESPOND_REQUIRED') {
        setAssignmentError(code);
      } else {
        showErrorToast(
          t(ErrorStringKeys.FAILED_TO_UPDATE_USERS_EMERGENCY_CONFIGURATION, { user: userName, configuration: newConfig.name }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isLocked = !canBeAssignedToRespondConfig || Boolean(assignmentError);

  const selector = (
    <FormControl variant="outlined" sx={{ width: '100%' }}>
      <Select
        value={selectedId}
        disabled={loading || !canBeAssignedToRespondConfig || disabled}
        onChange={(e) => {
          if (disabled) return;
          const selected = configList.find((c) => c.id === e.target.value);
          if (selected) {
            setSelectedId(selected.id);
            setShouldForceApply(false);
            setPendingConfig(selected);
          }
        }}
        displayEmpty
        sx={{
          backgroundColor: palette.grey[300],
          border: 'none',
          borderRadius: 2,
          height: 40,
          fontSize,
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            border: `solid 2px ${COLORS.BLUE_CRAYOLA}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
      >
        {configList.map((c) => (
          <MenuItem key={c.id} value={c.id} sx={{ fontSize }}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      {user && (
        <Box sx={{ width: '80%', minWidth: 120, position: 'relative' }}>
          {isLocked && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(1px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 22, color: COLORS.GREY }} />
            </Box>
          )}
          {isLocked ? (
            <LightTooltip
              title={
                <PlanExplainer
                  reason={assignmentError ?? 'PLAN_RESPOND_REQUIRED'}
                  compact
                  bodyColor={COLORS.SAFEGUARD_BLUE}
                  bodyFontSize="13px"
                />
              }
              placement="top"
              sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                },
              }}
              slotProps={{
                popper: {
                  modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
                },
              }}
            >
              {/* Select is disabled here, so the hover target is this wrapping span rather than the (pointer-events-restricted) control itself. */}
              <span style={{ display: 'block' }}>{selector}</span>
            </LightTooltip>
          ) : (
            selector
          )}
        </Box>
      )}

      <BaseModal
        open={confirmOpen}
        onClose={() => {
          setSelectedId(prevId);
          setPendingConfig(null);
          setShouldForceApply(false);
        }}
        title={t('update_configuration')}
        onSubmit={() => {
          if (pendingConfig) updateUser(pendingConfig, shouldForceApply);
          setPendingConfig(null);
          setShouldForceApply(false);
        }}
        submitButtonText="Apply"
        size="sm"
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('choose_how_configuration_change_should_be_applied')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <RadioOptionBox
            selected={!shouldForceApply}
            onClick={() => setShouldForceApply(false)}
            label={t('require_worker_confirmation')}
            description={t('require_worker_confirmation_description')}
          />
          <RadioOptionBox
            selected={shouldForceApply}
            onClick={() => setShouldForceApply(true)}
            label={t('apply_without_worker_confirmation')}
            description={t('apply_without_worker_confirmation_description')}
          />
        </Box>
      </BaseModal>

      <LoadingData show={loading} />
    </>
  );
};

export default EmergencyConfigPicker;
